/**
 * Legacy URL redirects — the map of retired paths that still earn inbound
 * links, and where each one now lives.
 *
 * These are applied at the Worker edge (see `handleFetch` in
 * `src/cloudflare-entry.ts`) rather than as TanStack route files, for the same
 * reason the trailing-slash rule lives there: the redirect must be a real 301
 * on the raw request, before SSR, so crawlers stop re-fetching the dead URL.
 * A route-level `throw redirect()` would need a full SSR pass per crawl hit.
 * A click inside the app never reaches the edge, so the root route reads the
 * same map before it navigates (`appLegacyRedirect`): an old link in a saved
 * notification or an old NOVA answer still opens its page.
 *
 * Add a rule here whenever a public path is renamed or removed while external
 * pages, articles or backlinks still point at the old one. Keep it a pure
 * function of the pathname — no DB, no request context — so it stays unit
 * testable and free at the edge.
 *
 * **Match retired paths exactly, never a live prefix.** Running ahead of the
 * router is what makes these redirects cheap, and also what makes a broad rule
 * dangerous: `/section/*` silently shadows every real route under `/section`,
 * including ones added later. Enumerate the dead paths instead, and cover each
 * new rule with a test asserting the live neighbours are left alone. A prefix
 * rule is right only for a prefix whose whole subtree moved, so that no live
 * page is left under it.
 *
 * Aliases: legacy redirects, 301 map, retired urls, old url redirect
 */

import {
  ARTICLES_INDEX_PATH,
  blogAuthorRedirectTarget,
  blogPostRedirectTarget,
} from "~/lib/shared/legacy-blog-redirects";
import { MARKETING_SUITE_PAGE_PATH } from "~/lib/shared/marketing-suite";
import {
  READINESS_PAGE_PATH,
  READINESS_PUBLIC_PATH,
} from "~/lib/shared/readiness/routes";

type LegacyRule = {
  /** Matches the retired path (and, where relevant, captures a slug). */
  readonly pattern: RegExp;
  /** Builds the destination from the match. Return null to skip the rule. */
  readonly to: (match: RegExpMatchArray) => string | null;
};

/**
 * A path segment we are willing to carry into a destination URL. Deliberately
 * narrow: a redirect target is built by string concatenation, so anything that
 * could smuggle a scheme, host, backslash or dot-segment must never reach it.
 */
const SAFE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/iu;

const safeSlug = (value: string | undefined): string | null =>
  value && SAFE_SLUG_PATTERN.test(value) ? value.toLowerCase() : null;

/**
 * The rest of a moved coach page's path (`/assets/icp`, `/weeks/3`), carried
 * over as it is. Ids, slugs and numbers only, for the same reason as
 * {@link SAFE_SLUG_PATTERN}.
 */
const SAFE_TAIL_PATTERN = /^(?:\/[A-Za-z0-9_-]+)*$/u;

const safeTail = (value: string | undefined): string | null =>
  SAFE_TAIL_PATTERN.test(value ?? "") ? (value ?? "") : null;

/**
 * Coach pages that moved under their menu section, so that the address names
 * the section the menu shows. Sent emails, saved notifications, old NOVA
 * answers, Stripe returns and bookmarks keep the old addresses, so these
 * rules stay.
 */
const MOVED_COACH_PAGES: ReadonlyMap<string, string> = new Map([
  ["/my-coaching/readiness-score", READINESS_PAGE_PATH],
  ["/my-coaching/marketing-suite", MARKETING_SUITE_PAGE_PATH],
  ["/my-coaching/grow", "/my-coaching/business/reading"],
  ["/my-coaching/dashboard", "/my-coaching/clients/dashboard"],
  ["/my-coaching/invoices", "/my-coaching/clients/invoices"],
  ["/my-coaching/clients", "/my-coaching/clients/programs"],
  ["/my-coaching/clients/new", "/my-coaching/clients/programs/new"],
]);

/** Program ids are UUIDs (`engagements.id`). */
const PROGRAM_ID_PATTERN =
  "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

const RULES: readonly LegacyRule[] = [
  // The retired blog. The tables list exact slugs, so a post or author that is
  // not in them, a new one included, stays a 404 instead of landing somewhere
  // unrelated.
  {
    pattern: /^\/blog(?:\/members)?$/iu,
    to: () => ARTICLES_INDEX_PATH,
  },
  {
    pattern: /^\/blog\/author\/([^/]+)$/iu,
    to: (match) => blogAuthorRedirectTarget(match[1] ?? ""),
  },
  {
    pattern: /^\/blog\/([^/]+)$/iu,
    to: (match) => blogPostRedirectTarget(match[1] ?? ""),
  },
  // Challenge pages were renamed `/challenges/*` → `/coaching-for/*`. CIH
  // framework pages still link to the old form.
  {
    pattern: /^\/challenges(?:\/([^/]+))?$/iu,
    to: (match) => {
      const raw = match[1];
      if (raw === undefined) return "/coaching-for";
      const slug = safeSlug(raw);
      return slug ? `/coaching-for/${slug}` : "/coaching-for";
    },
  },
  // Coach Business Builder never had per-tier pages (`/6-figure-club`,
  // `/accelerator`, `/7-figure-club`); the coaching-business guides link to
  // them anyway, so those three land on the real product page.
  //
  // Deliberately NOT a `/coach-business-builder/*` catch-all. Because these
  // rules run at the edge, ahead of TanStack Router, a prefix rule shadows
  // every real route underneath it — here the `/coach-business-builder` layout
  // route itself. An unknown subpath is better left to 404 than silently
  // swallowed.
  {
    pattern:
      /^\/coach-business-builder\/(6-figure-club|accelerator|7-figure-club)$/iu,
    to: () => "/page/coach-business-builder",
  },
  // The CCO landing page lives at `/page/for-ccos` and always has. The "Start a
  // conversation" CTA on `/page/partner` links to the bare `/for-ccos`, which
  // has never existed — that href is Builder.io content, fixed in the CMS
  // rather than here. This rule covers the same path wherever else it was
  // written the same way (older campaign emails, external posts), so the bare
  // form stops 404ing even after the CTA is corrected.
  {
    pattern: /^\/for-ccos$/iu,
    to: () => "/page/for-ccos",
  },
  // The 6-Figure Readiness Score replaced the Business Health Score. Ads,
  // emails and articles still carry both of its public addresses, and sent
  // reminder emails carry the old in-app page.
  {
    pattern:
      /^\/(?:business-health-score|coach-business-builder\/diagnostic)$/iu,
    to: () => READINESS_PUBLIC_PATH,
  },
  {
    pattern: /^\/my-coaching\/nova\/prcpr$/iu,
    to: () => READINESS_PAGE_PATH,
  },
  // The Content Studio's rooms and Library were retired: Diagnose became the
  // score page, and every other room (and the coming-soon Refine room) and
  // the Library became My assets. Saved
  // bookmarks and old NOVA answers carry both the old `/my-coaching/nova/…`
  // form and the `/my-coaching/business/nova/…` one; both land in one hop.
  {
    pattern:
      /^\/my-coaching(?:\/business)?\/nova\/(?:room\/(diagnose|plan|build|grow|refine)|library)$/iu,
    to: (match) =>
      match[1]?.toLowerCase() === "diagnose"
        ? READINESS_PAGE_PATH
        : "/my-coaching/business/nova/assets",
  },
  // Coach pages that moved one for one; the table holds exact paths only.
  {
    pattern: /^\/my-coaching\/.+$/iu,
    to: (match) => MOVED_COACH_PAGES.get(match[0].toLowerCase()) ?? null,
  },
  // NOVA, the courses and the lead magnets moved whole under My business: no
  // live page is left under their old prefix, so each keeps its tail.
  {
    pattern: /^\/my-coaching\/(nova|courses|lead-magnets)((?:\/[^/]+)*)$/iu,
    to: (match) => {
      const tail = safeTail(match[2]);
      return tail === null
        ? null
        : `/my-coaching/business/${match[1].toLowerCase()}${tail}`;
    },
  },
  // One program and its subpages. Only a program id matches, so Dashboard,
  // Client programs and Invoices, which now share the `/my-coaching/clients/`
  // prefix, are never caught.
  {
    pattern: new RegExp(
      `^/my-coaching/clients/(${PROGRAM_ID_PATTERN})((?:/[^/]+)*)$`,
      "iu",
    ),
    to: (match) => {
      const tail = safeTail(match[2]);
      return tail === null
        ? null
        : `/my-coaching/clients/programs/${match[1].toLowerCase()}${tail}`;
    },
  },
];

/**
 * The 301 destination for a retired public path, or `null` when the path is
 * not a legacy URL (the overwhelmingly common case — callers should treat
 * `null` as "carry on with the normal request").
 *
 * `pathname` is expected to be already trailing-slash-normalized, which the
 * Worker guarantees by running its trailing-slash 301 first. Never returns the
 * input path, so a rule can't produce a redirect loop.
 */
export function resolveLegacyRedirect(pathname: string): string | null {
  for (const rule of RULES) {
    const match = pathname.match(rule.pattern);
    if (!match) continue;
    const destination = rule.to(match);
    if (destination && destination !== pathname) return destination;
  }
  return null;
}

/**
 * The current address of an in-app href (`/path?query#hash`) that may name a
 * retired path, with its query and hash kept, or `null` when the path is
 * current. The router reads it before every navigation; a NOVA link card
 * reads it to name the page an old link leads to.
 */
export function appLegacyRedirect(href: string): string | null {
  const suffixAt = href.search(/[?#]/u);
  const pathname = suffixAt === -1 ? href : href.slice(0, suffixAt);
  const suffix = suffixAt === -1 ? "" : href.slice(suffixAt);
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/u, "") : pathname;
  const destination = resolveLegacyRedirect(normalized);
  return destination === null ? null : `${destination}${suffix}`;
}
